import { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import classes from './tutorWidget.module.scss'
import { useMyActions } from "../../../../entities/my";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { ITutor, ITutorChange, ITutorData, TutorCard, tutorService, useTutorActions } from "../../../../entities/tutor";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { TutorCalendar } from "../calendar/TutorCalendar";
import { DeleteAction } from "../../../../features/deleteAction";
import { getStaffDetailPath, getStaffReturnPath } from '../../../../app/router/staffNavigation';
import { StudentsMove } from "../../../../features/studentsMove";
import { Modal } from "../../../../shared/ui/modal";
import { ConfirmationAction } from "../../../../shared/ui/confirmationAction";
import { MyTextarea } from "../../../../shared/ui/myTextarea";
import { MyButton, MyButtonSecondary } from "../../../../shared/ui/button";
import editImg from '../../../../shared/lib/assets/edit.png';
import folderPlus from '../../../student/lib/assets/folderPlus.png';
import folderMinus from '../../../student/lib/assets/folderMinus.png';
import { useAppSelector } from "../../../../app/store/store";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { Dropdown } from "../../../../shared/ui/dropdown";
import { FEATURES, hasFeature, hasFeatureGrant } from "../../../../entities/my";
import { documentsService, fileToBase64 } from "../../../../entities/documents";
import { MoveUserWorkTg } from "../../../../features/moveUserWorkTg";
import { SanctionSequenceResetAction } from "../../../../features/sanctionSequenceReset";

const getMskTodayIso = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date())
    const getPart = (type: string) => parts.find(part => part.type === type)?.value || ''
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`
}

interface IProps {
    id: number;
    isStudentsMove: boolean;
    setIsStudentsMove: (isStudentsMove: boolean) => void; 
    staffMode?: boolean;
    headerOnly?: boolean;
    prefetchedTutor?: ITutorData | null;
    editPath?: string;
    canResetPassword?: boolean;
    onResetPassword?: () => void;
    onTutorLoaded?: (tutor: ITutorData) => void;
    onTutorArchiveChange?: (tutor: ITutorData) => void;
    onTutorSanctionSequenceReset?: (tutor: ITutorData) => void;
    canMoveWorkTg?: boolean;
    onWorkTgMoveSuccess?: () => Promise<void>;
}

export const TutorWidget: FC<IProps & PropsWithChildren> = ({id, isStudentsMove, setIsStudentsMove, staffMode = false, headerOnly = false, prefetchedTutor = null, editPath, canResetPassword = false, onResetPassword, onTutorLoaded, onTutorArchiveChange, onTutorSanctionSequenceReset, canMoveWorkTg = false, onWorkTgMoveSuccess}) => {

    const [isLoaing, setIsLoading] = useState<boolean>(true)
    const [tutor, setTutor] = useState<ITutorData>()
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setTutor: setTutorChange} = useTutorActions()
    const [archiveOpen, setArchiveOpen] = useState<boolean>(false)
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false)
    const [archiveComment, setArchiveComment] = useState<string>('')
    const [archiveCommentError, setArchiveCommentError] = useState<string>('')
    const [terminationFile, setTerminationFile] = useState<File | null>(null)
    const [terminationUploadAt, setTerminationUploadAt] = useState<string>('')
    const [terminationError, setTerminationError] = useState<string>('')
    const {setIsLoading: setIsLoadingGlobal} = useGlobalLoadingActions()
    const {my} = useAppSelector(s => s.myReducer)
    const tutorUserId = tutor?.user_id || prefetchedTutor?.user_id || id
    const tutorMoveUserId = tutorUserId || tutor?.id || id
    const canResetSanctionSequence = hasFeature(my, FEATURES.TUTORS.SANCTION_SEQUENCE_RESET, tutorUserId)
    const canEditSanctionSequenceAction = canResetSanctionSequence
    const canTransferStudents = hasFeature(my, FEATURES.TUTORS.STUDENTS_TRANSFER, tutorUserId)
        && hasFeatureGrant(my, FEATURES.IDM.STAFF_LIST)
    const canDeleteTutor = hasFeature(my, FEATURES.TUTORS.DELETE, tutorUserId)
    const canArchiveTutor = staffMode
        ? tutor?.is_archive
            ? hasFeature(my, FEATURES.TUTORS.UNARCHIVE, tutorUserId)
            : hasFeature(my, FEATURES.IDM.STAFF_ARCHIVE, tutorUserId)
        : tutor?.is_archive
            ? hasFeature(my, FEATURES.TUTORS.UNARCHIVE, tutorUserId)
            : hasFeature(my, FEATURES.TUTORS.ARCHIVE, tutorUserId)
    const canUpdateTutor = staffMode && editPath
        ? hasFeature(my, FEATURES.IDM.PERSONAL_UPDATE, tutorUserId)
            || hasFeature(my, FEATURES.TUTORS.UPDATE, tutorUserId)
            || (
                hasFeature(my, FEATURES.WORK_TGS.LIST)
                && hasFeature(my, FEATURES.IDM.WORK_TGS_UPDATE, tutorUserId)
            )
        : hasFeature(my, FEATURES.TUTORS.UPDATE, tutorUserId)
    const mainWorkTgId = tutor?.work_tg_ids?.[0]
    const hasStaffDropdownActions = canTransferStudents
        || (staffMode && canResetPassword && Boolean(onResetPassword))
        || (staffMode && canMoveWorkTg && Boolean(mainWorkTgId || tutor?.work_tg_name))
        || canEditSanctionSequenceAction

    const router = useNavigate()
    const {search} = useLocation()
    const backPath = getStaffReturnPath(search, 'performers')

    const getData = async () => {
        try{
            if(prefetchedTutor){
                setTutor(prefetchedTutor)
                return
            }
            if(id){
                setIsLoading(true)
                const tutorRes = await tutorService.get(+id)
                setTutor(tutorRes)
            }
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении данных о исполнителе', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(prefetchedTutor){
            setTutor(prefetchedTutor)
            setIsLoading(false)
            return
        }
        if(staffMode && headerOnly){
            setIsLoading(false)
            return
        }
        getData()
    }, [id, prefetchedTutor, staffMode, headerOnly])

    useEffect(() => {
        if(tutor){
            onTutorLoaded?.(tutor)
        }
    }, [onTutorLoaded, tutor])

    const onDelete = async () => {
        if(tutor){
            if(staffMode && tutorUserId > 0){
                await tutorService.deleteByUserId(tutorUserId)
            }
            else{
                await tutorService.delete(tutor.id)
            }
            router(backPath)
        }
    }

    const openArchiveModal = () => {
        setArchiveComment('')
        setArchiveCommentError('')
        setTerminationFile(null)
        setTerminationUploadAt(getMskTodayIso())
        setTerminationError('')
        setArchiveConfirmOpen(false)
        setArchiveOpen(true)
    }

    const onArchivePrepare = () => {
        if(tutor?.is_archive){
            setArchiveConfirmOpen(true)
            return
        }

        const comment = archiveComment.trim()
        if(!comment){
            setArchiveCommentError('Укажите причину архивации')
            return
        }

        setArchiveConfirmOpen(true)
    }

    const onArchiveToggle = async () => {
        if(!tutor) return
        try{
            setIsLoadingGlobal(true)
            if(tutor.is_archive){
                if(staffMode && tutorUserId > 0){
                    await tutorService.unarchiveByUserId(tutorUserId)
                }
                else{
                    await tutorService.unarchive(tutor.id)
                }
                const updatedTutor = {...tutor, is_archive: false, archive_comment: ''}
                setTutor(updatedTutor)
                onTutorArchiveChange?.(updatedTutor)
            } else {
                const comment = archiveComment.trim()
                if(staffMode){
                    if(!tutorUserId){
                        throw new Error('Не удалось определить сотрудника для архивации')
                    }
                    const terminationDocument = terminationFile ? {
                        termination_file_name: terminationFile.name,
                        termination_content_type: terminationFile.type || 'application/octet-stream',
                        termination_content: await fileToBase64(terminationFile),
                        termination_upload_at: terminationUploadAt || getMskTodayIso(),
                    } : {}
                    await documentsService.archiveUser(tutorUserId, {
                        comment,
                        ...terminationDocument,
                    })
                } else {
                    await tutorService.archive(tutor.id, comment)
                }
                const updatedTutor = {...tutor, is_archive: true, archive_comment: comment}
                setTutor(updatedTutor)
                onTutorArchiveChange?.(updatedTutor)
            }
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при архивировании или разархивировании исполнителя', type: 'error'})
            }
        }
        finally{
            setIsLoadingGlobal(false)
            setArchiveOpen(false)
        }
    }

    const onMove = useCallback((_tutor: ITutor) => {
        setIsStudentsMove(true)
    }, [setIsStudentsMove])

    const onEdit = useCallback(() => {
        if(tutor){
            if(editPath){
                router(editPath)
                return
            }
            const tutorChange: ITutorChange = {
                id: tutor?.id,
                user_id: tutor.user_id,
                tag_ids: tutor.tag_ids ?? [],
                email: tutor.email,
                tg: tutor.tg,
                work_tg_name: tutor.work_tg_name || '',
                work_tg_ids: tutor.work_tg_ids ?? [],
                full_name: tutor.full_name,
                cost_per_hour: tutor.cost_per_hour,
                phone: tutor.phone,
            }
            setTutorChange(tutorChange)
            const targetUserId = Number(tutor.user_id || tutor.id)
            router(getStaffDetailPath(targetUserId, 'performers', search, '/update'))
        }
    }, [editPath, router, search, setTutorChange, tutor])

    return (
        <section className={classes.container}>   
            {
                isLoaing
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                tutor
                    &&
                <>
                        <section className={classes.header}>
                        <section className={classes.heading}>
                            <section className={classes.title}>
                                <span>{(staffMode && headerOnly ? tutor.user_id || tutor.id : tutor.id)}. {tutor.full_name}</span>
                                {staffMode && <span
                                    className={tutor.is_archive ? classes.archiveStatus : classes.activeStatus}
                                    title={tutor.is_archive ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                    aria-label={tutor.is_archive ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                    data-status-tooltip={tutor.is_archive ? 'Сотрудник в архиве' : 'Сотрудник активен'}
                                >
                                    {tutor.is_archive
                                        ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6L18 18M18 6L6 18" /></svg>
                                        : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5L9.5 17L19 7" /></svg>}
                                </span>}
                            </section>
                        </section>
                        <section className={classes.actions}>
                        {
                            staffMode && hasStaffDropdownActions &&
                            <Dropdown
                                title={<span className={classes.actionSign}>Действия</span>}
                            >
                                <section className={classes.dropdownActions}>
                                    {
                                        canTransferStudents && !isStudentsMove &&
                                        <section className={classes.dropdownActionItem}>
                                            <StudentsMove
                                                title="Перенести студентов к другому исполнителю"
                                                tutorId={tutorMoveUserId}
                                                request={staffMode ? tutorService.getAllV1.bind(tutorService) : undefined}
                                                onSelected={onMove}
                                            />
                                        </section>
                                    }
                                    {
                                        staffMode && canMoveWorkTg && (mainWorkTgId || tutor.work_tg_name) && onWorkTgMoveSuccess &&
                                        <section className={classes.dropdownActionItem}>
                                            <MoveUserWorkTg
                                                userId={tutorUserId}
                                                currentTgId={mainWorkTgId}
                                                currentTgName={tutor.work_tg_name}
                                                onSuccess={onWorkTgMoveSuccess}
                                            />
                                        </section>
                                    }
                                    {
                                        canResetPassword && onResetPassword &&
                                        <section className={classes.dropdownActionItem}>
                                            <MyButton onClick={onResetPassword}>Сбросить пароль</MyButton>
                                        </section>
                                    }
                                    {canEditSanctionSequenceAction && (
                                        <section className={classes.dropdownActionItem}>
                                            <SanctionSequenceResetAction
                                                value={tutor?.sanction_sequence_number ?? prefetchedTutor?.sanction_sequence_number}
                                                canEdit={canEditSanctionSequenceAction}
                                                onUpdate={async (value) => {
                                                    await tutorService.updateSanctionSequence(tutorUserId, value)
                                                    const updatedTutor = await tutorService.getByUserId(tutorUserId)
                                                    setTutor(updatedTutor)
                                                    onTutorSanctionSequenceReset?.(updatedTutor)
                                                }}
                                                onSuccess={() => {}}
                                            />
                                        </section>
                                    )}
                                </section>
                            </Dropdown>
                        }
                        {
                            !staffMode && canArchiveTutor
                                &&
                            <section
                                className={classes.archive}
                                onClick={openArchiveModal}
                            >
                                {
                                    tutor.is_archive
                                        ?
                                    <>
                                        <img src={folderMinus} alt="" />
                                        Разархивировать<br/> исполнителя
                                    </>
                                        :
                                    <>
                                        <img src={folderPlus} alt="" />
                                        Архивировать<br/> исполнителя
                                    </>
                                }
                            </section>
                        }
                        {
                            canUpdateTutor
                                &&
                            <button
                                type="button"
                                className={classes.edit}
                                onClick={onEdit}
                                title="Редактировать сотрудника"
                                aria-label="Редактировать сотрудника"
                            >
                                <img src={editImg} alt="" />
                            </button>
                        }
                        {
                            staffMode && canArchiveTutor &&
                            <button
                                type="button"
                                className={`${classes.archiveToggle} ${tutor.is_archive ? classes.unarchive : ''}`}
                                onClick={openArchiveModal}
                                title={tutor.is_archive ? 'Разархивировать сотрудника' : 'Архивировать сотрудника'}
                                aria-label={tutor.is_archive ? 'Разархивировать сотрудника' : 'Архивировать сотрудника'}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 3V12" />
                                    <path d="M7.05 5.05A8 8 0 1 0 16.95 5.05" />
                                </svg>
                            </button>
                        }
                        {
                            canDeleteTutor &&
                            <DeleteAction
                                successText="Исполнитель удален"
                                errorText="Ошибка при удалении исполнителя"
                                onDelete={onDelete}
                                questionText="Точно хотите удалить Исполнителя ?"
                            >
                                <button type="button" className={classes.deleteToggle} title="Удалить сотрудника" aria-label="Удалить сотрудника">
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M4 7H20" />
                                        <path d="M10 11V17M14 11V17" />
                                        <path d="M6 7L7 20H17L18 7" />
                                        <path d="M9 7V4H15V7" />
                                    </svg>
                                </button>
                            </DeleteAction>
                        }
                        <Modal setOpen={setArchiveOpen} open={archiveOpen}>
                            {
                                tutor.is_archive || archiveConfirmOpen
                                    ?
                                <ConfirmationAction
                                    onClick={onArchiveToggle}
                                    setOpen={setArchiveOpen}
                                    title={`Вы точно хотите ${tutor.is_archive ? 'разархивировать' : 'архивировать'} исполнителя ?`}
                                    type='send'
                                />
                                    :
                                <section className={classes.archiveCommentModal}>
                                    <h3>Причина архивации</h3>
                                    <p>Укажите причину архивации. Она сохранится в карточке исполнителя.</p>
                                    <MyTextarea
                                        title="Причина"
                                        value={archiveComment}
                                        setValue={setArchiveComment}
                                        setError={setArchiveCommentError}
                                        placeholder="Например: исполнитель больше не работает"
                                        rows={5}
                                    />
                                    {staffMode && <>
                                        <label className={classes.terminationField}>Договор о расторжении (необязательно)
                                            <input type="file" onChange={event => { setTerminationFile(event.target.files?.[0] || null); setTerminationError('') }} />
                                        </label>
                                        {terminationError && <section className={classes.archiveCommentError}>*{terminationError}</section>}
                                    </>}
                                    {
                                        archiveCommentError &&
                                        <section className={classes.archiveCommentError}>*{archiveCommentError}</section>
                                    }
                                    <section className={classes.archiveCommentButtons}>
                                        <MyButtonSecondary onClick={() => setArchiveOpen(false)}>
                                            Отмена
                                        </MyButtonSecondary>
                                        <MyButton onClick={onArchivePrepare}>
                                            Продолжить
                                        </MyButton>
                                    </section>
                                </section>
                            }
                        </Modal>
                        </section>
                    </section>
                    {
                        !headerOnly &&
                        <>
                            <TutorCard
                                tutor={tutor}
                                hideFinanceSection={staffMode}
                            >
                                <>
                                    <TutorCalendar id={tutor.id} userId={tutor.user_id} />
                                </>
                            </TutorCard>
                            {
                                !isStudentsMove && !staffMode && canTransferStudents
                                    &&
                                <section className={classes.move}>
                                    <StudentsMove
                                        title="Перенести студентов к новому исполнителю"
                                        tutorId={tutorMoveUserId}
                                        onSelected={onMove}
                                        />
                                </section>
                            }
                        </>
                    }
                </>
            }
        </section>
    )
}
